import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";
import { ProfileHeader } from "@/components/User/ProfileHeader";
import { GeoSignalsBar } from "@/components/User/GeoSignalsBar";
import { ProfileTabs, ProfileTabContent } from "@/components/User/ProfileTabs";
import { AboutTab } from "@/components/User/AboutTab";
import { ProfilePromptsGrid } from "@/components/User/ProfilePromptsGrid";
import { SavedPromptsGrid } from "@/components/User/SavedPromptsGrid";
import { useUserProfile } from "@/hooks/user/useUserProfile";
import { useCreatorStats } from "@/hooks/user/useCreatorStats";

const UserProfile = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("prompts");
  
  const { profile, loading } = useUserProfile(userId || "");
  const stats = useCreatorStats(userId || "");

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/prompts");
    }
  };

  const handleEditProfile = () => {
    navigate("/profile");
  };

  if (loading || stats.loading) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardContent className="p-6 text-center">
              <h2 className="text-2xl font-bold mb-2">User Not Found</h2>
              <p className="text-muted-foreground mb-4">
                The user you're looking for doesn't exist or has been removed.
              </p>
              <Button onClick={() => navigate("/prompts")}>
                Go to Prompts
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const isOwnProfile = user?.id === userId;

  return (
    <Layout>
      <div className="container max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={handleBack}
          className="text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Profile Header */}
        <ProfileHeader
          userId={profile.id}
          username={profile.username}
          avatarId={profile.avatarId}
          bio={profile.bio}
          promptCount={stats.totalPrompts}
          isOwnProfile={isOwnProfile}
          badges={profile.badges}
          creatorTier={stats.creatorTier}
          onEditProfile={handleEditProfile}
        />

        {/* GEO Signals Bar */}
        <GeoSignalsBar
          totalPrompts={stats.totalPrompts}
          totalCopies={stats.totalCopies}
          avgGeoScore={stats.avgGeoScore}
          creatorTier={stats.creatorTier}
          lastActive={stats.lastActive}
        />
        
        {/* Profile Tabs */}
        <ProfileTabs activeTab={activeTab} onTabChange={setActiveTab} isOwnProfile={isOwnProfile}>
          <ProfileTabContent value="prompts">
            <ProfilePromptsGrid userId={userId || ""} isOwnProfile={isOwnProfile} />
          </ProfileTabContent>
          
          {isOwnProfile && (
            <ProfileTabContent value="saved">
              <SavedPromptsGrid userId={userId || ""} />
            </ProfileTabContent>
          )}
          
          <ProfileTabContent value="about">
            <AboutTab 
              bio={profile.bio}
              longBio={profile.longBio}
              focusTags={profile.focusTags}
              externalLinks={profile.externalLinks}
              createdAt={profile.createdAt}
              username={profile.username}
              isOwnProfile={isOwnProfile}
              onEditProfile={isOwnProfile ? handleEditProfile : undefined}
            />
          </ProfileTabContent>
        </ProfileTabs>
      </div>
    </Layout>
  );
};

export default UserProfile;
